import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

// PrimeReact
import { Card } from "primereact/card";
import { Tag } from "primereact/tag";

// Toolkit
import { C, themeClasses as T } from "sj-ui-toolkit/src/atlas/utils/themeClasses";
import { getThemeLogo } from "sj-ui-toolkit/src/helpers";
import { useThemeSwitch } from "sj-ui-toolkit/src/theme";
import { IS_PRODUCTION_MODE } from "sj-ui-toolkit/src/utils/env";

const tld = IS_PRODUCTION_MODE ? "com" : "dev";

const groups = [
  {
    name: "Security Journey domains",
    domains: [
      {
        displayDomain: `my.securityjourney.${tld}`,
        requestedDomains: [`my.securityjourney.${tld}`],
      },
      {
        displayDomain: `*.my.securityjourney.${tld}`,
        requestedDomains: [`app.sb.my.securityjourney.${tld}`],
      },
      {
        displayDomain: `auth.hackedu.${tld}`,
        requestedDomains: [`auth.hackedu.${tld}`],
      },
    ],
  },
  {
    name: "Video domains",
    domains: [
      { displayDomain: "vimeo.com", requestedDomains: ["vimeo.com"] },
      {
        displayDomain: "*.vimeo.com",
        requestedDomains: [
          "player.vimeo.com",
          "f.vimeocdn.com",
          "i.vimeocdn.com",
          "vod-adaptive-ak.vimeocdn.com",
        ],
      },
    ],
  },
  {
    name: "Platform tool domains",
    domains: [
      {
        displayDomain: "*.launchdarkly.com",
        requestedDomains: [
          "app.launchdarkly.com",
          "clientstream.launchdarkly.com",
          "events.launchdarkly.com",
        ],
      },
      {
        displayDomain: "browser-intake-datadoghq.com",
        requestedDomains: ["browser-intake-datadoghq.com"],
      },
    ],
  },
];

const allRequestedDomains = groups.reduce<string[]>((acc, group) => {
  group.domains.forEach(({ requestedDomains }) => {
    requestedDomains.forEach(domain => {
      if (!acc.includes(domain)) {
        acc.push(domain);
      }
    });
  });
  return acc;
}, []);

type DomainStatus = {
  isConnected: boolean;
  latency?: number;
  error?: string;
  timestamp: string;
};

const TestConnection = () => {
  const { appliedTheme } = useThemeSwitch();
  const themeLogo = useMemo(() => getThemeLogo(appliedTheme), [appliedTheme]);
  const [status, setStatus] = useState<Record<string, DomainStatus>>({});
  const allTested = allRequestedDomains.every(domain => status[domain] !== undefined);

  const testDomain = async (domain: string) => {
    try {
      const startTime = performance.now();
      await fetch(`https://${domain}`, {
        mode: "no-cors",
        cache: "no-cache",
      });
      const endTime = performance.now();
      const latency = Math.round(endTime - startTime);

      setStatus(prev => ({
        ...prev,
        [domain]: {
          isConnected: true,
          latency,
          timestamp: new Date().toISOString(),
        },
      }));
    } catch (error) {
      setStatus(prev => ({
        ...prev,
        [domain]: {
          isConnected: false,
          error: error instanceof Error ? error.message : String(error),
          timestamp: new Date().toISOString(),
        },
      }));
    }
  };

  useEffect(() => {
    allRequestedDomains.forEach(domain => testDomain(domain));
    // Test connections every 30 seconds
    const interval = setInterval(() => {
      allRequestedDomains.forEach(domain => testDomain(domain));
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  /**
   * Returns a tri-state for a display-domain row:
   *  "pending"       – at least one underlying domain hasn't been tested yet
   *  "connected"     – all underlying domains responded successfully
   *  "not connected" – all underlying domains have been tested and at least one failed
   */
  const getDisplayDomainState = (requestedDomains: string[]) => {
    if (requestedDomains.some(domain => status[domain] === undefined)) {
      return "pending";
    }
    if (requestedDomains.every(domain => status[domain]?.isConnected)) {
      return "connected";
    }
    return "not connected";
  };

  return (
    <main className="atlas-bg-home h-full">
      <header
        style={{
          height: "var(--navbar-height)",
          paddingLeft: "40px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Link to="/" aria-label="Go to dashboard">
          <img
            alt="Security Journey Logo"
            src={themeLogo}
            style={{ height: "auto", width: "100px", maxHeight: "40px", objectFit: "contain" }}
          />
        </Link>
      </header>
      <Card className="mb-4 max-w-4xl mx-auto">
        <div className="flex items-center gap-2 mb-4">
          <i className="pi pi-wifi text-2xl" aria-hidden="true" />
          <h1 className="atlas-h3 m-0">Testing Browser Connection</h1>
        </div>

        <div role="status" aria-live="polite" aria-busy={!allTested} className="sr-only">
          {allTested
            ? "Connection test complete."
            : "Testing browser connection to required domains…"}
        </div>

        <div className="flex flex-col gap-4">
          {groups.map(group => (
            <div key={group.name} className={C(T.bgElevation1, "p-4 rounded")}>
              <h2 className="atlas-h5 mb-3">{group.name}</h2>
              <ul className="flex flex-col gap-2 list-none p-0 m-0">
                {group.domains.map(({ displayDomain, requestedDomains }) => {
                  const domainState = getDisplayDomainState(requestedDomains);
                  const isPending = domainState === "pending";
                  const isConnected = domainState === "connected";
                  const maxLatency = isConnected
                    ? Math.max(...requestedDomains.map(domain => status[domain]?.latency || 0))
                    : 0;

                  return (
                    <li key={displayDomain} className="flex items-center gap-2 body-md">
                      {isPending ? (
                        <i className="pi pi-spin pi-spinner" aria-hidden="true" />
                      ) : isConnected ? (
                        <i className="pi pi-link text-green-500" aria-hidden="true" />
                      ) : (
                        <i className="pi pi-link error-text" aria-hidden="true" />
                      )}
                      <span>{displayDomain}</span>
                      <span className="sr-only">
                        {isPending ? "testing" : isConnected ? "connected" : "not connected"}
                      </span>
                      {isConnected && (
                        <Tag
                          value={`${maxLatency}ms`}
                          severity="success"
                          className="ml-2"
                          aria-label={`latency ${maxLatency} milliseconds`}
                        />
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </Card>
    </main>
  );
};

export default TestConnection;
